import Color from 'color';
import type { Palette } from '@mui/material/styles';
import type { CustomColors } from '../ThemeRegistry/theme.types';

/**
 * Determines contrast text color based on background luminance
 * @param bgColor - Background color to check contrast against
 * @returns Contrast text color (black or white)
 */
const getContrastText = (baseColor: string): string => {
  const baseColorObj = Color(baseColor);
  return baseColorObj.isLight()
    ? baseColorObj.mix(Color('#000000'), 0.9).hex()
    : baseColorObj.mix(Color('#ffffff'), 0.9).hex();
};

export function generateTheme(baseColor: string): Partial<Palette> {
  const bgColor = baseColor;

  const isLight = Color(baseColor).isLight();

  const primaryColor = getPrimaryColor(bgColor);
  const secondaryColor = getSecondaryColor(bgColor);

  const bgPaperColor = getPaperBgColor(bgColor);
  const bgPrimaryContrastText = getContrastText(bgColor);

  return {
    mode: isLight ? 'light' : 'dark',
    background: {
      default: bgColor,
      paper: bgPaperColor,
      contrastText: bgPrimaryContrastText
    },
    primary: {
      main: primaryColor
    },
    secondary: {
      main: secondaryColor
    },
    text: {
      primary: bgPrimaryContrastText
    }
  };
}

const getPrimaryColor = (baseColor: string): string => {
  const bgColorObj = Color(baseColor);

  // For black, white, or very light/dark colors
  if (bgColorObj.isLight()) {
    // For white or very light colors
    if (bgColorObj.lightness() > 90) {
      return bgColorObj.lightness(40).saturate(0.5).hex();
    }
    // For light colors
    return bgColorObj.rotate(90).lightness(80).saturate(0.5).hex();
  } else {
    // For black or very dark colors
    if (bgColorObj.lightness() < 10) {
      return bgColorObj.lightness(60).saturate(0.5).hex();
    }
    // For dark colors
    return bgColorObj.rotate(180).lightness(60).saturate(0.5).hex();
  }
};

const getSecondaryColor = (baseColor: string): string => {
  const bgColorObj = Color(baseColor);

  return bgColorObj.isLight() ? bgColorObj.darken(0.25).hex() : bgColorObj.lighten(0.25).hex();
};

const getPaperBgColor = (bgColor: string): string => {
  const bgColorObj = Color(bgColor);
  return bgColorObj.isLight() ? bgColorObj.darken(0.05).hex() : bgColorObj.lighten(0.05).hex();
};

/**
 * Generate all custom colors based on a base color
 * @param baseColor Base color in hex format
 * @returns Object containing all custom colors
 */
const generateCustomColors = (baseColor: string): CustomColors => {
  const baseColorObj = Color(baseColor);

  return {
    black: { main: '#000000' },
    white: { main: '#ffffff' },
    light: { main: baseColorObj.lighten(0.7).desaturate(0.5).hex() },
    dark: { main: baseColorObj.darken(0.7).desaturate(0.2).hex() },
    gray: { main: baseColorObj.desaturate(0.9).hex() },
    vibrant: { main: baseColorObj.rotate(120).saturate(0.2).hex() },
    accent: { main: baseColorObj.rotate(90).hex() },
    neutral: { main: baseColorObj.desaturate(0.7).hex() },
    transparentLight: { main: '#000000' },
    transparentDark: { main: '#ffffff' }
  };
};
